/**
 * Brain tools for the tactics surface — Phase 1 of the tactics-hub
 * roadmap (`docs/scope/tactics-hub-roadmap.md`).
 *
 * Two tools:
 *   - draft_tactics_pattern  — produces a TacticsLayout payload the
 *                              coach can commit to the active board.
 *   - critique_tactics_pattern — analyses the current layout (already
 *                              in the Brain's screen context) and
 *                              returns structured geometry facts so
 *                              Claude can compose a human-readable
 *                              critique without hallucinating coords.
 *
 * The Brain already sees the current TacticsLayout via the screen-
 * context block in `brainPrompt.ts`, so a separate `read_tactics_
 * pattern` tool isn't needed yet. Future phases (multi-keyframe,
 * branching) will add read tools when there's more state than fits
 * in the context window.
 */
import type { AnthropicTool } from '@4cm/core/ai';
import type {
  TacticsLayout,
  TacticsPlayerSpot,
  TacticsArrow,
} from '@4cm/core/tactics';

// World coordinate caps — pitch is 105 × 68, centred at origin.
const X_LIMIT = 34;
const Z_LIMIT = 52.5;

/**
 * Player-spot input from the Brain. Loose typing on the wire (the
 * model speaks plain JSON), tightened to TacticsPlayerSpot on apply.
 */
interface TacticsSpotInput {
  role?: string;
  x?: number;
  z?: number;
}

interface TacticsArrowInput {
  points?: Array<{ x?: number; z?: number }>;
  color?: string;
  player_key?: string;
}

interface TacticsDraftInput {
  persist_key?: string;
  home_formation?: string;
  away_formation?: string;
  home_spots?: TacticsSpotInput[];
  away_spots?: TacticsSpotInput[];
  arrows?: TacticsArrowInput[];
  /** One-sentence description shown on the preview card. */
  intent?: string;
}

/**
 * Tool schemas registered with the Brain. Mirror the
 * structure of `TacticsLayout` but accept loose types so model output
 * survives small JSON variations.
 */
export const TACTICS_DRAFT_TOOL: AnthropicTool = {
  name: 'draft_tactics_pattern',
  description:
    'Propose a tactics layout (formation + player positions + movement arrows) ' +
    'as a preview card for coach approval. Does NOT save. Use whenever the coach ' +
    'asks to set up a formation, sketch a press, position players, or build a ' +
    'tactical idea. The current board state is in the screen-context block — ' +
    'reference it for spatial accuracy. Coordinates: world metres, pitch-centred; ' +
    'x ∈ [-34, +34] sideline, z ∈ [-52.5, +52.5] goal-line (home defends +z, ' +
    'attacks toward -z).',
  input_schema: {
    type: 'object',
    properties: {
      persist_key: {
        type: 'string',
        description:
          'localStorage key for the target board (e.g. "tactics-3d-{sessionId}"). ' +
          'Defaults to the active board from screen context.',
      },
      home_formation: {
        type: 'string',
        description: 'Formation code, e.g. "4-3-3", "4-2-3-1", "3-5-2".',
      },
      away_formation: { type: 'string' },
      home_spots: {
        type: 'array',
        description:
          '11 home positions. Role drives the 3D body archetype (GK / CB / LW / etc).',
        items: {
          type: 'object',
          properties: {
            role: { type: 'string' },
            x: { type: 'number', description: 'Sideline axis, world metres.' },
            z: { type: 'number', description: 'Goal-line axis, world metres.' },
          },
          required: ['role', 'x', 'z'],
        },
      },
      away_spots: {
        type: 'array',
        description:
          'Optional. 11 away positions. Omit to keep the existing away layout untouched.',
        items: {
          type: 'object',
          properties: {
            role: { type: 'string' },
            x: { type: 'number' },
            z: { type: 'number' },
          },
          required: ['role', 'x', 'z'],
        },
      },
      arrows: {
        type: 'array',
        description:
          'Movement arrows. Bind to a player via player_key ("home-7") to make the run ' +
          'animate that token during playback; omit player_key for passive annotation.',
        items: {
          type: 'object',
          properties: {
            points: {
              type: 'array',
              items: {
                type: 'object',
                properties: {
                  x: { type: 'number' },
                  z: { type: 'number' },
                },
                required: ['x', 'z'],
              },
            },
            color: {
              type: 'string',
              description: 'CSS hex. Conventions: #4ade80 home green, #fbbf24 away amber, #f5f5f5 neutral.',
            },
            player_key: { type: 'string', description: 'Optional binding, e.g. "home-7".' },
          },
          required: ['points'],
        },
      },
      intent: {
        type: 'string',
        description:
          'One-sentence description of what this pattern accomplishes — shown on the preview card so the coach knows what they\'re approving.',
      },
    },
    required: ['home_formation', 'home_spots', 'intent'],
  },
};

export const TACTICS_CRITIQUE_TOOL: AnthropicTool = {
  name: 'critique_tactics_pattern',
  description:
    'Inspect the current tactics layout (from screen context) and return structured ' +
    'geometry facts: line gaps between defensive blocks, lateral compactness, depth, ' +
    'player isolation. Use the facts to compose a human-readable critique back to the ' +
    'coach. Does not mutate the board.',
  input_schema: {
    type: 'object',
    properties: {
      persist_key: {
        type: 'string',
        description: 'Defaults to the active board from screen context.',
      },
    },
  },
};

export const TACTICS_TOOLS: AnthropicTool[] = [
  TACTICS_DRAFT_TOOL,
  TACTICS_CRITIQUE_TOOL,
];

/** Names registered in the dispatcher's switch statement. */
export const TACTICS_TOOL_NAMES = new Set<string>([
  'draft_tactics_pattern',
  'critique_tactics_pattern',
]);

// ---------------------------------------------------------------------------
// Critique — pure geometry analysis on a TacticsLayout
// ---------------------------------------------------------------------------

export interface TacticsCritiqueFacts {
  /** Lateral spread of the back line (xmax − xmin among CBs / LB / RB). */
  defensive_line_width: number | null;
  /** Vertical depth (zmax − zmin) of the back line. */
  defensive_line_depth: number | null;
  /** Gap between back-line z mean and midfield z mean. */
  back_to_mid_gap: number | null;
  /** Gap between midfield z mean and forward-line z mean. */
  mid_to_fwd_gap: number | null;
  /** Players whose nearest team-mate is more than 18 m away. */
  isolated_players: string[];
  /** Count of arrows bound to a player vs free annotations. */
  bound_runs: number;
  free_arrows: number;
  /** True when the team has < 2 players in their own half — open to counter. */
  exposed_to_counter: boolean;
}

const BACK_LINE_ROLES = new Set(['GK', 'LB', 'CB', 'RB', 'LWB', 'RWB', 'SW']);
const MID_ROLES = new Set(['CDM', 'CM', 'CAM', 'DM', 'AM', 'LM', 'RM']);
const FWD_ROLES = new Set(['LW', 'RW', 'ST', 'CF', 'SS']);

function meanZ(spots: TacticsPlayerSpot[]): number | null {
  if (spots.length === 0) return null;
  return spots.reduce((s, p) => s + p.z, 0) / spots.length;
}

function widthRange(spots: TacticsPlayerSpot[]): number | null {
  if (spots.length === 0) return null;
  const xs = spots.map((p) => p.x);
  return Math.max(...xs) - Math.min(...xs);
}

function depthRange(spots: TacticsPlayerSpot[]): number | null {
  if (spots.length === 0) return null;
  const zs = spots.map((p) => p.z);
  return Math.max(...zs) - Math.min(...zs);
}

/**
 * Compute critique facts from a layout. Pure function; safe to import
 * from anywhere (no DOM / storage / network).
 */
export function critiqueTactics(layout: TacticsLayout): TacticsCritiqueFacts {
  const home = layout.homeSpots;

  const backLine = home.filter((s) => BACK_LINE_ROLES.has(s.role.toUpperCase()) && s.role.toUpperCase() !== 'GK');
  const midLine = home.filter((s) => MID_ROLES.has(s.role.toUpperCase()));
  const fwdLine = home.filter((s) => FWD_ROLES.has(s.role.toUpperCase()));

  const backMeanZ = meanZ(backLine);
  const midMeanZ = meanZ(midLine);
  const fwdMeanZ = meanZ(fwdLine);

  const isolated: string[] = [];
  for (const p of home) {
    const others = home.filter((q) => q !== p);
    if (others.length === 0) continue;
    const minDist = Math.min(
      ...others.map((q) => Math.hypot(q.x - p.x, q.z - p.z)),
    );
    if (minDist > 18) isolated.push(`${p.role} at (${p.x.toFixed(0)}, ${p.z.toFixed(0)})`);
  }

  const arrowsByBinding = (layout.arrows ?? []).reduce(
    (acc, a: TacticsArrow) => {
      if (a.playerKey) acc.bound++;
      else acc.free++;
      return acc;
    },
    { bound: 0, free: 0 },
  );

  // Own half for home = +z half. Players in own half = z > 0.
  const ownHalfCount = home.filter((p) => p.z > 0).length;

  return {
    defensive_line_width: widthRange(backLine),
    defensive_line_depth: depthRange(backLine),
    back_to_mid_gap:
      backMeanZ !== null && midMeanZ !== null ? backMeanZ - midMeanZ : null,
    mid_to_fwd_gap:
      midMeanZ !== null && fwdMeanZ !== null ? midMeanZ - fwdMeanZ : null,
    isolated_players: isolated,
    bound_runs: arrowsByBinding.bound,
    free_arrows: arrowsByBinding.free,
    exposed_to_counter: ownHalfCount < 2,
  };
}

// ---------------------------------------------------------------------------
// Apply: turn a draft payload into a TacticsLayout safe to write
// ---------------------------------------------------------------------------

function clampX(v: number): number {
  return Math.max(-X_LIMIT, Math.min(X_LIMIT, v));
}
function clampZ(v: number): number {
  return Math.max(-Z_LIMIT, Math.min(Z_LIMIT, v));
}

function normaliseSpot(input: TacticsSpotInput): TacticsPlayerSpot | null {
  if (
    !input ||
    typeof input.role !== 'string' ||
    typeof input.x !== 'number' ||
    typeof input.z !== 'number'
  ) {
    return null;
  }
  return {
    role: input.role,
    x: clampX(input.x),
    y: 0.6, // figure-height in 3D; ignored in 2D. Default to the 3D token height.
    z: clampZ(input.z),
  };
}

function normaliseArrow(
  input: TacticsArrowInput,
  fallbackColor: string,
): TacticsArrow | null {
  if (!input?.points || !Array.isArray(input.points)) return null;
  const points = input.points
    .filter((p): p is { x: number; z: number } =>
      typeof p?.x === 'number' && typeof p?.z === 'number',
    )
    .map((p) => ({ x: clampX(p.x), z: clampZ(p.z) }));
  if (points.length < 2) return null;
  return {
    id: `brain-${Date.now()}-${Math.floor(Math.random() * 1e6)}`,
    points,
    color: typeof input.color === 'string' ? input.color : fallbackColor,
    playerKey: input.player_key,
  };
}

/**
 * Build a TacticsLayout from a draft payload. Caller passes the
 * existing layout (if any) so partial drafts (e.g. home_spots only,
 * away untouched) merge cleanly. All world coords are clamped to the
 * pitch on entry so AI-hallucinated values never escape.
 *
 * Returns null if the draft has no usable home spots — caller can
 * surface "draft was empty" to the coach.
 */
export function applyTacticsDraft(
  draft: TacticsDraftInput,
  existing: TacticsLayout | null,
): TacticsLayout | null {
  const homeSpots = (draft.home_spots ?? [])
    .map(normaliseSpot)
    .filter((s): s is TacticsPlayerSpot => s !== null);
  if (homeSpots.length === 0) return null;

  const awaySpots = draft.away_spots
    ? draft.away_spots
        .map(normaliseSpot)
        .filter((s): s is TacticsPlayerSpot => s !== null)
    : (existing?.awaySpots ?? []);

  const arrows = (draft.arrows ?? [])
    .map((a) => normaliseArrow(a, '#4ade80'))
    .filter((a): a is TacticsArrow => a !== null);

  return {
    v: 1,
    homeFormation: draft.home_formation ?? existing?.homeFormation ?? '4-3-3',
    awayFormation: draft.away_formation ?? existing?.awayFormation ?? '4-3-3',
    homeSpots,
    awaySpots,
    arrows,
    saved_at: new Date().toISOString(),
  };
}
